import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { useCustomer } from 'hooks/useSession'
import { getErpDate } from '@ca/common/types'
import { useDateAgo } from '@ui/elements/DateAgo'
import Row, { TextItem, textProps } from '@ui/elements/SectionRow/NestedRow'
import CollapsibleSectionRow from '@ui/elements/SectionRow/CollapsibleSectionRow'
import { OrderStatusBadge } from '@order/OrderHistory/OrderBadge'
import Typography from '@ui/typography'
import { colors } from '@ui/colors'
import Icon from '@ui/elements/Icon'

import { SectionProps } from '../types'
import { View } from 'react-native'
import cn from '@ui/utils/cn'

type Props = SectionProps

export default function GeneralSection({ data }: Props) {
  const { t } = useTranslation(['ui', 'common', 'order'])
  const header = 'order:details.sections.general.header'
  const label = 'order:details.labels'
  const dateAgo = useDateAgo()
  const customer = useCustomer()

  const {
    id,
    ref,
    customer_id,
    invoice_id,
    staff,
    note,
    createdAt,
    updatedAt,
    store,
  } = data

  const storeName = store ? store.name : t('common:store')

  return (
    <CollapsibleSectionRow
      icon="shopping-bag-outlined"
      title={t(`${header}.title`)}
      desc={t(`${header}.desc`)}
    >
      <Row
        icon="info-circle"
        title={t(`${label}.status`)}
        body={<OrderStatusBadge data={data} />}
      />
      <Row icon="hash" title={t(`${label}.ids`)} texts={[ref, `${id}`]} />
      <Row
        icon="calendar-plus"
        title={t(`${label}.createdAt`)}
        texts={[
          format(getErpDate(createdAt), 'dd.MM.yyyy HH:mm:ss'),
          dateAgo(getErpDate(createdAt)),
        ]}
      />
      <Row
        icon="calendar-up"
        title={t(`${label}.updatedAt`)}
        texts={[
          format(getErpDate(updatedAt), 'dd.MM.yyyy HH:mm:ss'),
          dateAgo(getErpDate(updatedAt)),
        ]}
      />
      <Row
        icon="building-store"
        title={t(`${label}.store`)}
        body={
          store && (
            <View className="min-w-0 flex-1 flex-row justify-end gap-6">
              <Typography
                {...textProps}
                className={cn(
                  textProps.className,
                  'min-w-0 shrink truncate text-right'
                )}
                numberOfLines={1}
              >
                {storeName}
              </Typography>
              <View className="shrink-0 flex-row">
                <TextItem value={store.ref} index={1} />
              </View>
              <View className="shrink-0 flex-row">
                <TextItem value={store.id?.toString()} index={2} />
              </View>
            </View>
          )
        }
        texts={!store ? [t('common:store')] : undefined}
      />
      <Row
        icon="shield"
        title={t(`${label}.staff`)}
        texts={
          staff && typeof staff === 'object'
            ? [staff.name, staff.ref, staff.id.toString()]
            : [t('common:staff')]
        }
      />
      <Row
        icon="user-circle"
        title={t(`${label}.customer`)}
        texts={[
          customer_id === customer?.id
            ? `${customer?.first_name} ${customer?.last_name}`
            : t('ui:customer'),
          customer?.ref,
          customer_id.toString(),
        ]}
      />
      <Row
        icon="file-invoice"
        title={t(`${label}.billing`)}
        texts={[invoice_id.toString()]}
      />
      {/* <Row icon="hierarchy" title={t(`${label}.related`)} texts={[data.related_id]} /> */}
      <Row
        last
        icon="message-circle-2"
        title={t(`${label}.note`)}
        body={
          typeof note === 'string' ? (
            <Typography variant="XS" color={colors.black50}>
              {note}
            </Typography>
          ) : (
            <Icon
              name={note ? 'check' : 'minus'}
              color={note ? colors.green[500] : colors.neutral[300]}
            />
          )
        }
      />
    </CollapsibleSectionRow>
  )
}
